import { Col, Row } from "antd/es/grid";
import { CloseModal } from "../../../hooks/useModal";
import BaseDialog from "../../../components/BaseDialog";
import DialogFooter from "../../../components/DialogFooter";
import {
  ITEM_TYPE_ENUM,
  ORDER_STATUS_ENUM,
  SALE_VALUE_TYPE_ENUM,
  SALE_VALUE_TYPE_OPTION,
} from "../../../../applications/constants/AppConst";
import { useEffect, useMemo, useRef, useState } from "react";
import AppInputNumber from "../../../components/AppInputNumber";
import {
  OrderItemMappingRequest,
  OrderResponse,
} from "../../../../infrastructure/model/OrderModel";
import usePageState from "../../../hooks/usePageState";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { currencyFormat } from "../../../../applications/utils/NumberUtils";
import dayjs from "dayjs";
import { useItemsContext } from "../../../context/AppItemsContext";
import { useReactToPrint } from "react-to-print";
import Visible from "../../../components/Visible";
import "../styles/OrderBillDialog.scss";
import AppInputText from "../../../components/AppInputText/AppInputText";
import Select from "antd/es/select";
import { useToppingContext } from "../../../context/AppToppingContext";
import {
  getItemInOrderUseCase,
  updateOrderCounterDiscountUseCase,
  updateOrderStatusAndUserInfoUseCase,
} from "../../../../applications/useCase/ApiUseCase/OrderApiUseCase";
import Assets from "../../../../applications/assets";

interface OrderBillDialogProps {
  onClose: CloseModal<boolean>;
  orderItem: OrderResponse;
  isTakeAway?: boolean;
}

function OrderBillDialog(props: OrderBillDialogProps) {
  const { onClose, orderItem, isTakeAway = true } = props;
  const { items } = useItemsContext();
  const [feeShip, setFeeShip] = useState<number>(0);
  const [counterDiscount, setCounterDiscount] = useState<number>(0);
  const [discountReason, setDiscountReason] = useState<string>("");
  const leftInputRef = useRef<HTMLDivElement>(null);
  const [counterDiscountType, setCounterDiscountType] = useState<SALE_VALUE_TYPE_ENUM>(
    SALE_VALUE_TYPE_ENUM.VND,
  );
  const [customerName, setCustomerName] = useState<string>(orderItem.username || "");
  const [customerPhone, setCustomerPhone] = useState<string>(orderItem.user_phone || "");
  const [customerAddress, setCustomerAddress] = useState<string>(orderItem.address || "");
  const [rightWidth, setRightWidth] = useState<string>("300px");
  const { toppings } = useToppingContext();
  const [itemMappings, setItemMappings] = useState<OrderItemMappingRequest[]>([]);
  const { setLoading, isLoading, showError } = usePageState();
  const printRef = useRef<HTMLDivElement>(null);
  const [employeeName, setEmployeeName] = useState<string>("");

  async function onUpdateOrderStatusAndInfo(
    orderId: number,
    newStatus: ORDER_STATUS_ENUM,
    name?: string,
    phone?: string,
    address?: string,
  ) {
    const res = await updateOrderStatusAndUserInfoUseCase({
      orderId,
      newStatus,
      name,
      phone,
      address,
      setLoading,
      showError,
    });
    if (res) {
      onClose();
    }
  }

  async function onUpdateCounterDiscount() {
    const res = await updateOrderCounterDiscountUseCase({
      request: {
        order_id: orderItem.id,
        counter_discount: counterDiscountByVND,
      },
      setLoading,
      showError,
    });
    return res;
  }

  const reactToPrintFn = useReactToPrint({
    contentRef: printRef,
    onAfterPrint: async () => {
      await onUpdateOrderStatusAndInfo(
        orderItem?.id,
        ORDER_STATUS_ENUM.COMPLETE,
        customerName,
        customerPhone,
        customerAddress,
      );
      await onUpdateCounterDiscount();
    },
    onPrintError: (errorLocation, error) => {
      showError(error.message);
    },
  });

  async function getItemsInOrder(orderId: number) {
    const res = await getItemInOrderUseCase({
      orderId,
      setLoading,
      showError,
    });
    if (res) {
      setItemMappings(res);
    }
  }

  const selectCurrencyAfter = () => {
    const options = SALE_VALUE_TYPE_OPTION?.filter((_, i) => i !== 2);
    return (
      <Select
        defaultValue={counterDiscountType}
        style={{ width: 80 }}
        onChange={(e) => setCounterDiscountType(e)}
      >
        {options?.map((s, index) => {
          return <Select.Option value={s.value}>{s.label}</Select.Option>;
        })}
      </Select>
    );
  };

  const counterDiscountByVND = useMemo(() => {
    if (counterDiscountType === SALE_VALUE_TYPE_ENUM.PERCENT) {
      return (orderItem.discount_price * counterDiscount) / 100;
    }
    return counterDiscount;
  }, [counterDiscount, counterDiscountType]);

  const totalItemCount = useMemo(() => {
    if (!itemMappings?.length || !items?.length) return 0;

    const excludedItemIds = new Set(
      items
        .filter((p) => [ITEM_TYPE_ENUM.TOPPING, ITEM_TYPE_ENUM.OTHER].includes(p.type))
        .map((p) => p.id),
    );

    return itemMappings.reduce((sum, it) => {
      if (excludedItemIds.has(it.item_id)) return sum;
      return sum + it.item_quantity;
    }, 0);
  }, [itemMappings, items]);

  function groupOrderItems(items: OrderItemMappingRequest[]) {
    const map = new Map<string, OrderItemMappingRequest>();

    items.forEach((item) => {
      const sortedToppings = [...(item.topping_ids || [])].sort((a, b) => a - b);

      const key = `${item.item_id}_${item.item_size}_${sortedToppings.join(",")}`;

      if (map.has(key)) {
        const existing = map.get(key)!;
        existing.item_quantity += item.item_quantity;
      } else {
        map.set(key, {
          ...item,
          topping_ids: sortedToppings,
        });
      }
    });

    return Array.from(map.values());
  }

  const groupedItemMappings = useMemo(() => {
    return groupOrderItems(itemMappings);
  }, [itemMappings]);

  useAsyncEffect(async () => {
    await getItemsInOrder(orderItem.id);
  }, []);

  useEffect(() => {
    if (leftInputRef.current) {
      setRightWidth(`calc(100% - ${leftInputRef.current.clientWidth || 0}px - 5px)`);
    }
  }, [leftInputRef.current]);

  return (
    <BaseDialog
      title="Hóa đơn thanh toán"
      loading={isLoading}
      width={"50vw"}
      footer={
        <DialogFooter
          submitProps={{
            onClick: reactToPrintFn,
            children: "In",
            disabled: orderItem.discount_price - counterDiscountByVND < 0,
          }}
          cancelProps={{
            onClick: () => onClose(undefined),
          }}
        />
      }
    >
      <Row>
        <Col
          span={8}
          style={{ borderRight: "2px solid #d3d5d7", paddingRight: "8px" }}
          ref={leftInputRef}
        >
          <Visible>
            <div className="w-100">
              <AppInputText
                orientation="col"
                label="Nhân viên tạo đơn"
                currentValue={employeeName}
                onTextChange={(e) => {
                  setEmployeeName(e);
                }}
              />
            </div>
            <div className="w-100">
              <AppInputText
                orientation="col"
                label="Tên khách hàng"
                currentValue={customerName}
                onTextChange={(e) => {
                  setCustomerName(e);
                }}
              />
            </div>
            <div className="w-100">
              <AppInputText
                orientation="col"
                label="SĐT khách hàng"
                currentValue={customerPhone}
                onTextChange={(e) => {
                  setCustomerPhone(e);
                }}
              />
            </div>
            <div className="w-100">
              <AppInputText
                orientation="col"
                label="Địa chỉ"
                currentValue={customerAddress}
                onTextChange={(e) => {
                  setCustomerAddress(e);
                }}
              />
            </div>
          </Visible>
          <Visible>
            <div className="w-100">
              <AppInputNumber
                orientation="col"
                label="Phí ship"
                currentValue={feeShip}
                min={0}
                onChangeValue={(e) => {
                  setFeeShip(e);
                }}
                suffix={"VNĐ"}
              />
            </div>
          </Visible>
          <div className="w-100">
            <AppInputNumber
              orientation="col"
              label="Giảm giá tại quầy"
              currentValue={counterDiscount}
              min={0}
              onChangeValue={(e) => {
                setCounterDiscount(e);
              }}
              addonAfter={selectCurrencyAfter()}
            />
            <AppInputText
              label="Lí do giảm giá"
              textType="TEXT"
              orientation="col"
              currentValue={discountReason}
              additionProps={{
                maxLength: 50,
                placeholder: "Lí do...",
              }}
              onTextChange={(e) => {
                setDiscountReason(e);
              }}
            />
          </div>
          <div className="w-100">
            <AppInputNumber
              orientation="col"
              label="Tổng tiền"
              disabled
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "") as unknown as number}
              currentValue={orderItem.discount_price + feeShip - counterDiscountByVND}
              suffix={"VNĐ"}
            />
          </div>
        </Col>
        <Col className="print-content-xprinter" ref={printRef} style={{ width: rightWidth }}>
          {/* Tiêu đề */}
          <Row justify="center" style={{ fontWeight: "bold" }}>
            Thanh toán hóa đơn
          </Row>
          <Row justify="center" style={{ marginTop: 8 }}>
            Mã HĐ: {orderItem.code}
          </Row>
          <Row justify="center" style={{ marginTop: 8 }}>
            Nhân viên: {employeeName || "___________________"}
          </Row>
          <Row justify="center" style={{ marginTop: 8 }}>
            {dayjs().format("DD/MM/YYYY HH:mm")}
          </Row>
          <Row style={{ marginTop: 8 }}>
            <Col span={24}>
              <Row>Tên: {customerName}</Row>
              <Row>SĐT: {customerPhone}</Row>
              <Row>Địa chỉ: {customerAddress}</Row>
            </Col>
          </Row>
          {/* Header bảng */}
          <Row className="flex justify-content-end">Tổng số lượng: {totalItemCount} cốc</Row>
          <Row
            style={{
              marginTop: "8px",
              border: "1px solid #d3d5d7",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            <Col span={2}>ID</Col>
            <Col span={10} style={{ borderLeft: "1px solid #d3d5d7" }}>
              Tên
            </Col>
            <Col span={2} style={{ borderLeft: "1px solid #d3d5d7" }}>
              SL
            </Col>
            <Col span={5} style={{ borderLeft: "1px solid #d3d5d7" }}>
              Đơn giá
            </Col>
            <Col span={5} style={{ borderLeft: "1px solid #d3d5d7" }}>
              Thành tiền
            </Col>
          </Row>

          {/* Dữ liệu */}
          {groupedItemMappings?.map((row, index) => {
            const currentItem = items.find((e) => e.id === row.item_id);
            const toppingOfItem = toppings?.filter((t) => row.topping_ids?.includes(t.id));
            const toppingNames = toppingOfItem?.map((t) => t.name).join(", ") || "";
            const toppingPrices =
              toppingOfItem?.reduce((sum, t) => {
                return sum + (t.price || 0);
              }, 0) || 0;
            const finalPrice =
              (currentItem?.size_mapping?.find((m) => m.size === row.item_size)?.price || 0) +
              toppingPrices;
            return (
              <Row
                key={index}
                style={{
                  borderLeft: "1px solid #d3d5d7",
                  borderRight: "1px solid #d3d5d7",
                  borderBottom: "1px solid #d3d5d7",
                  textAlign: "center",
                }}
              >
                <Col span={2}>{index + 1}</Col>
                <Col span={10} style={{ borderLeft: "1px solid #d3d5d7", wordBreak: "break-word" }}>
                  {currentItem?.name} {toppingNames?.trim()?.length > 0 ? ` (${toppingNames})` : ""}
                </Col>
                <Col span={2} style={{ borderLeft: "1px solid #d3d5d7" }}>
                  {row.item_quantity}
                </Col>
                <Col span={5} style={{ borderLeft: "1px solid #d3d5d7" }}>
                  {finalPrice.toLocaleString()}
                </Col>
                <Col span={5} style={{ borderLeft: "1px solid #d3d5d7" }}>
                  {(finalPrice * row.item_quantity).toLocaleString()}
                </Col>
              </Row>
            );
          })}
          <Row style={{ marginTop: 8 }}>
            <Col span={12}>Thành tiền</Col>
            <Col span={12} style={{ textAlign: "end" }}>
              {currencyFormat(orderItem.original_price)}
            </Col>
          </Row>
          <Row className="w-100 mt-2">
            <Col span={10}>
              <div>Giảm giá / chiết khấu</div>
              <div style={{ fontSize: "9px", fontStyle: "italic" }}>
                {orderItem?.apply_vouchers}
              </div>
            </Col>
            <Col span={14} style={{ textAlign: "end" }}>
              - {currencyFormat(orderItem.original_price - orderItem.discount_price)}
            </Col>
          </Row>
          <Row
            className="w-100 mt-2"
            style={{ display: counterDiscountByVND > 0 ? "flex" : "none" }}
          >
            <Col span={10}>
              <div>Giảm giá tại quầy</div>
              <div style={{ fontSize: "9px", fontStyle: "italic" }}>{discountReason}</div>
            </Col>
            <Col span={14} style={{ textAlign: "end" }}>
              - {currencyFormat(counterDiscountByVND)}
            </Col>
          </Row>
          <Row className="w-100 mt-2">
            <Col span={10}>
              <div>Tổng giảm giá</div>
            </Col>
            <Col span={14} style={{ textAlign: "end" }}>
              -{" "}
              {currencyFormat(
                orderItem.original_price - orderItem.discount_price + counterDiscountByVND,
              )}
            </Col>
          </Row>
          <Visible>
            <Row className="w-100 mt-2">
              <Col span={10}>Phí ship</Col>
              <Col span={14} style={{ textAlign: "end" }}>
                {currencyFormat(feeShip)}
              </Col>
            </Row>
          </Visible>

          <Row className="w-100 mt-2">
            <Col span={10}>Tổng tiền thanh toán</Col>
            <Col span={14} style={{ textAlign: "end" }}>
              {currencyFormat(feeShip + orderItem.discount_price - counterDiscountByVND)}
            </Col>
          </Row>
          <Row className="justify-content-center mt-2">
            <img src={Assets.QR_MONEY} alt="QR chuyển khoản" style={{ width: 120, height: 120 }} />
          </Row>
          <Row className="mt-2 justify-content-center">Toputii xin cảm ơn quý khách!</Row>
        </Col>
      </Row>
    </BaseDialog>
  );
}
export default OrderBillDialog;
